import React from 'react';

/**
 * Toegankelijke bouwstenen voor "kies dit"-rijen en -tegels.
 *
 * WAAROM DIT BESTAAT
 *
 * In het leerkrachtportaal worden keuzes (welk vak, welke onderdelen, welke
 * leerlingen) op meerdere plekken gebouwd als `<div onClick=...>` met een
 * getekend vinkje erin. Dat oogt goed en werkt met de muis, maar:
 *
 *   - het element krijgt geen focus, dus met het toetsenbord is het niet te
 *     bereiken — Tab slaat het over;
 *   - Enter en spatie doen niets, want er is geen knopgedrag;
 *   - een schermlezer hoort "groep, tekst" in plaats van "selectievakje,
 *     aangevinkt", dus de staat is onhoorbaar.
 *
 * Daarmee is het klaarzetten van een toets of huiswerk — de kernhandeling van
 * dit portaal — onbruikbaar zonder muis. Bij scholen weegt dat mee in de
 * inkoop, maar los daarvan: een leerkracht met een handblessure of een
 * schermlezer moet gewoon zijn werk kunnen doen.
 *
 * BEWUST GEEN GEDEELD "WIZARD"-COMPONENT
 *
 * De panelen voor toetsen en oefenwerk lijken op elkaar, maar zijn
 * inhoudelijk verschillende dingen: een toets meet en levert een oordeel op,
 * oefenwerk begeleidt en mag herhaald worden. Die twee samenvoegen tot één
 * component maakt elke latere wijziging aan de één een risico voor de ander.
 *
 * Wat ze wél delen is dit: hoe een keuze-element zich hoort te gedragen. Dat
 * is hier geïsoleerd, zodat de toegankelijkheid op één plek klopt zonder dat
 * de twee flows aan elkaar geknoopt worden.
 */

interface SelectableRowProps {
  /** Aangevinkt of niet. */
  selected: boolean;
  /** Wordt aangeroepen bij klik, Enter en spatie. */
  onToggle: () => void;
  /**
   * Wat de gebruiker kiest, in gewone taal. Een schermlezer leest dit voor,
   * dus "Optellen tot 100" en niet "familie 3".
   */
  label: string;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}

/**
 * Eén regel in een lijst waar er MEERDERE tegelijk aan kunnen staan.
 *
 * Rendert als echt selectievakje-gedrag (`role="checkbox"` + `aria-checked`),
 * zodat een schermlezer de staat meldt en de gebruiker weet dat er meer dan
 * één te kiezen valt.
 */
export function SelectableRow({
  selected,
  onToggle,
  label,
  disabled = false,
  className = '',
  children
}: SelectableRowProps) {
  return <div role="checkbox" aria-checked={selected} aria-label={label} aria-disabled={disabled || undefined} tabIndex={disabled ? -1 : 0} onClick={() => !disabled && onToggle()} onKeyDown={e => {
    // Spatie is de standaardtoets voor een selectievakje; Enter accepteren
    // we erbij omdat gebruikers die gewoontegetrouw proberen. In beide
    // gevallen het scrollen/versturen onderdrukken.
    if (disabled) return;
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      onToggle();
    }
  }} className={`focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1 ${className}`} data-id="element-9175" data-mp="1pultkyvfjpo4">
      
      {children}
    </div>;
}
interface SelectableCardProps {
  /** Geselecteerd of niet. */
  selected: boolean;
  /** Wordt aangeroepen bij klik, Enter en spatie. */
  onSelect: () => void;
  /** Wat de gebruiker kiest, in gewone taal. */
  label: string;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}

/**
 * Een tegel in een groep waar er precies ÉÉN gekozen wordt.
 *
 * Rendert als keuzerondje-gedrag (`role="radio"` + `aria-checked`). Het
 * verschil met SelectableRow is niet cosmetisch: een schermlezer meldt hierbij
 * dat het kiezen van deze tegel de vorige keuze vervangt, en dat is precies
 * wat er gebeurt.
 *
 * Zet de omhullende container op `role="radiogroup"` met een `aria-label`,
 * anders zweven de rondjes los van elkaar.
 */
export function SelectableCard({
  selected,
  onSelect,
  label,
  disabled = false,
  className = '',
  children
}: SelectableCardProps) {
  return <div role="radio" aria-checked={selected} aria-label={label} aria-disabled={disabled || undefined}
  // Binnen een radiogroep is alleen de actieve optie tabbaar; is er nog
  // niets gekozen, dan is de eerste bereikbaar. Dat laten we hier aan de
  // aanroeper over door simpelweg alle ingeschakelde tegels tabbaar te
  // maken — met een handvol opties is dat voorspelbaarder dan pijltjes-
  // navigatie die mensen niet verwachten in een formulier als dit.
  tabIndex={disabled ? -1 : 0} onClick={() => !disabled && onSelect()} onKeyDown={e => {
    if (disabled) return;
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      onSelect();
    }
  }} className={`focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-1 ${className}`} data-id="element-9176" data-mp="36d3ypz8rihd2">
      
      {children}
    </div>;
}